import { NavLink } from 'react-router-dom';
import { Lock } from 'lucide-react';
import type { SidebarNavItem } from '@/shared/utils/sidebar-nav';
import { useAccess, useUpgrade } from '@/shared/access';
import { NavIcon } from './nav-icon';
import { navLinkClass } from './nav-link-class';

interface Props {
    item:       SidebarNavItem;
    collapsed?: boolean;
    /** Rendered inside a group (see SidebarNavGroup) — indented under its label. */
    nested?:    boolean;
}

export function SidebarNavLink({ item, collapsed = false, nested = false }: Props) {
    const { can } = useAccess();
    const { requestUpgrade } = useUpgrade();

    // Visible-but-intercept: a gated module the member lacks renders as a button
    // that opens the Upgrade popup instead of navigating.
    const blocked = item.feature ? !can(item.feature).allowed : false;

    if (blocked) {
        return (
            <button
                type="button"
                onClick={() => requestUpgrade({ feature: item.feature, reason: can(item.feature!).reason })}
                className={navLinkClass({ isActive: false, collapsed, nested })}
                // Collapsed, the icon is all there is — name it for hover and
                // for screen readers.
                title={collapsed ? item.label : undefined}
                aria-label={collapsed ? item.label : undefined}
            >
                <NavIcon icon={item.icon} isActive={false} />
                {!collapsed && (
                    <>
                        <span className="flex-1 text-left">{item.label}</span>
                        <Lock className="h-3.5 w-3.5 text-ink-300" />
                    </>
                )}
            </button>
        );
    }

    return (
        <NavLink
            to={item.to}
            end={item.end}
            className={({ isActive }) => navLinkClass({ isActive, collapsed, nested })}
            title={collapsed ? item.label : undefined}
            aria-label={collapsed ? item.label : undefined}
        >
            {({ isActive }) => (
                <>
                    <NavIcon icon={item.icon} isActive={isActive} />
                    {!collapsed && (
                        <>
                            <span className="flex-1">{item.label}</span>
                            {item.tag && (
                                <span className="rounded w-100 bg-ink-100 px-1.5 py-0.5 font-ui text-[9px] font-semibold uppercase tracking-wider text-ink-500">
                                    {item.tag}
                                </span>
                            )}
                        </>
                    )}
                </>
            )}
        </NavLink>
    );
}
